import { getCountries, getCountryCallingCode, isSupportedCountry, parsePhoneNumberFromString } from 'libphonenumber-js/min'

const digits = value => String(value || '').replace(/\D/g, '')
const safeCountry = country => isSupportedCountry(country) ? country : 'AU'
const preferredCountries = ['AU', 'NZ', 'US', 'GB', 'CA', 'CN', 'HK', 'TW', 'SG']

export function getCheckoutCallingCode(country = 'AU') {
  return getCountryCallingCode(safeCountry(country))
}

export function getCheckoutPhoneCountries() {
  const names = new Intl.DisplayNames(['en'], { type: 'region' })
  const countries = getCountries().map(country => ({ country, name: names.of(country) || country, callingCode: getCountryCallingCode(country) }))
  return countries.sort((a, b) => {
    const left = preferredCountries.indexOf(a.country); const right = preferredCountries.indexOf(b.country)
    if (left >= 0 || right >= 0) return (left < 0 ? preferredCountries.length : left) - (right < 0 ? preferredCountries.length : right)
    return a.name.localeCompare(b.name, 'en')
  })
}

function resolvePhoneCountry(parsed, fallback) {
  if (parsed?.country) return parsed.country
  if (!parsed || getCheckoutCallingCode(fallback) === parsed.countryCallingCode) return fallback
  return [...preferredCountries, ...getCountries()].find(country => getCountryCallingCode(country) === parsed.countryCallingCode) || fallback
}

// Stored and submitted phone numbers use international digits without '+'.
export function splitCheckoutPhone(value, country = 'AU') {
  const number = digits(value)
  const fallback = safeCountry(country)
  const parsed = parsePhoneNumberFromString(`+${number}`, { extract: false })
  const resolvedCountry = resolvePhoneCountry(parsed, fallback)
  const callingCode = getCheckoutCallingCode(resolvedCountry)
  return { country: resolvedCountry, nationalNumber: parsed?.nationalNumber || (number.startsWith(callingCode) ? number.slice(callingCode.length) : number) }
}

export function normalizeCheckoutPhoneInput(value, country = 'AU') {
  const text = String(value || '').trim()
  const resolvedCountry = safeCountry(country)
  if (!text) return { country: resolvedCountry, nationalNumber: '', value: '' }
  const input = text.startsWith('00') ? `+${text.slice(2)}` : text
  const parsed = parsePhoneNumberFromString(input, { defaultCountry: resolvedCountry, extract: false })
  if (parsed) return { country: resolvePhoneCountry(parsed, resolvedCountry), nationalNumber: parsed.nationalNumber, value: digits(parsed.number) }
  const nationalNumber = digits(input)
  return { country: resolvedCountry, nationalNumber, value: nationalNumber ? `${getCheckoutCallingCode(resolvedCountry)}${nationalNumber}` : '' }
}

export function validateCheckoutPhone(value, country) {
  const text = String(value || '').trim()
  const number = digits(text)
  if (country && !isSupportedCountry(country)) return false
  if (!/^[+\d\s().-]+$/.test(text) || !/^[1-9]\d{1,14}$/.test(number)) return false
  const parsed = parsePhoneNumberFromString(`+${number}`, { extract: false })
  if (!parsed?.isPossible()) return false
  if (country && parsed.countryCallingCode !== getCheckoutCallingCode(country)) return false
  // Retain the Australian mobile contract while accepting other calling codes.
  return parsed.countryCallingCode !== '61' || /^614\d{8}$/.test(number)
}
